/*
 * Observabilidad de fallos en el cliente.
 *
 * `global-error.tsx` sólo recoge lo que escapa del árbol de React. Todo error
 * atrapado localmente —y en esta base son 109 `console.error` en bloques
 * `catch`— es invisible como incidencia: llega a Sentry como línea de log, sin
 * stack, sin agrupación y sin alerta.
 *
 * De ahí las dos piezas, con la misma división que en el backend:
 *
 *  - `instalarRedDeFallos` envuelve `window.fetch`. Es el punto de paso
 *    obligado: los 59 archivos que llaman a `fetch` pasan por ahí sin tener que
 *    saberlo, y también los que se escriban mañana.
 *  - `reportarFallo` es lo que un `catch` llama para mandar la excepción real
 *    con su stack.
 */
import * as Sentry from '@sentry/nextjs';
import { urlSinQuery } from './saneado';

/** Sólo interesan las llamadas al backend propio; el ruido de terceros no. */
function esApiPropia(url: string): boolean {
  const base = process.env.NEXT_PUBLIC_API_URL;
  if (!base) return url.startsWith('/api');
  return url.startsWith(base) || url.startsWith('/api');
}

// Por el nombre y no por `instanceof Error`: la cancelación es un DOMException, que no
// hereda de Error en todos los entornos.
function esCancelacionDeliberada(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { name?: unknown }).name === 'AbortError';
}

function urlDe(entrada: RequestInfo | URL): string {
  if (typeof entrada === 'string') return entrada;
  if (entrada instanceof URL) return entrada.toString();
  return entrada.url;
}

/**
 * Reporta una excepción con su stack desde un `catch`. Devuelve el id del
 * evento para poder mostrarlo al terapeuta y cruzarlo con Sentry sin pedirle
 * que reproduzca el fallo.
 */
export function reportarFallo(
  error: unknown,
  contexto: Record<string, unknown> = {}
): string {
  const excepcion = error instanceof Error ? error : new Error(String(error));

  return Sentry.captureException(excepcion, {
    tags: { origen: String(contexto.origen ?? 'catch') },
    extra: contexto
  });
}

/**
 * Envuelve `window.fetch` para que toda respuesta 5xx y todo fallo de red del
 * backend propio generen un evento, los reporte el componente o no.
 *
 * No lleva stack útil —la pila es siempre la del wrapper— pero responde a la
 * pregunta que hoy nadie puede responder: si la subida a GCS está fallando
 * sistemáticamente para un subconjunto de usuarios, ¿alguien se entera?
 */
export function instalarRedDeFallos(): void {
  if (typeof window === 'undefined') return;
  // Idempotente: el instrumentation-client puede evaluarse más de una vez en
  // desarrollo y envolver dos veces duplicaría cada evento.
  if ((window.fetch as { __redInstalada?: boolean }).__redInstalada) return;

  const original = window.fetch.bind(window);

  const envuelto = async (entrada: RequestInfo | URL, opciones?: RequestInit): Promise<Response> => {
    const url = urlDe(entrada);
    const metodo = opciones?.method ?? 'GET';

    try {
      const respuesta = await original(entrada, opciones);

      if (respuesta.status >= 500 && esApiPropia(url)) {
        Sentry.captureMessage(`${metodo} ${urlSinQuery(url)} respondió ${respuesta.status}`, {
          level: 'error',
          tags: { origen: 'respuesta5xx', metodo, estado: String(respuesta.status) },
          extra: { url: urlSinQuery(url) }
        });
      }
      return respuesta;
    } catch (error) {
      // Un fetch que lanza es fallo de red o CORS: nunca llegó al servidor, así
      // que el backend no puede saberlo. Si no se reporta aquí, no se reporta.
      // Salvo la cancelación deliberada (AbortError, p. ej. una recarga que
      // sustituye a la anterior): no es un fallo. Un TimeoutError sí lo es.
      if (esApiPropia(url) && !esCancelacionDeliberada(error)) {
        Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
          tags: { origen: 'fetchFallido', metodo },
          extra: { url: urlSinQuery(url) }
        });
      }
      throw error;
    }
  };

  (envuelto as { __redInstalada?: boolean }).__redInstalada = true;
  window.fetch = envuelto as typeof window.fetch;
}
